import { QueryClient, type QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    try {
      const errorResponse = await res.json();
      const errorData = errorResponse.data || errorResponse;
      const message = errorData.message || res.statusText;

      // Create an error with status code information
      const error = new Error(`${res.status}: ${message}`);
      (error as any).status = res.status;
      throw error;
    } catch (parseError) {
      if (parseError instanceof Error && parseError.message.includes(':')) {
        throw parseError;
      }
      const text = (await res.text()) || res.statusText;
      const error = new Error(`${res.status}: ${text}`);
      (error as any).status = res.status;
      throw error;
    }
  }
}


type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
    async ({ queryKey }) => {
      const res = await fetch(queryKey[0] as string, {
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      const responseData = await res.json();
      return responseData.data || responseData;
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      staleTime: 5 * 60 * 1000,
      retry: (_failureCount, error) => {
        // Don't retry on 401 errors - let fetchWithAuth handle token refresh
        if (error instanceof Error && error.message.startsWith('401:')) {
          return false;
        }
        // Retry other errors up to 2 times
        return _failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (_failureCount, error) => {
        // Don't retry on 401 errors for mutations either
        if (error instanceof Error && error.message.startsWith('401:')) {
          return false;
        }
        // Don't retry mutations by default
        return false;
      },
    },

  },
});